import React from "react";
import { Button, Grid, MenuItem } from "@mui/material";
import { Field, Form, Formik, FormikProps } from "formik";
import { CheckboxWithLabel, TextField } from "formik-mui";
import { useTranslation } from "react-i18next";

import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ButtonLoading } from "src/components/ButtonLoading";
import { APIError, User, UserRole } from "src/types";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { useUserSchema } from "src/schemas/user.schema";

export type UserFormData = Partial<User> & Pick<User, "firstName" | "lastName" | "email" | "role" | "enabled">;

interface Props {
  data: UserFormData;
  onClose: () => void;
  onLoginLinkRequest?: () => void;
  onSubmit: (values: UserFormData) => any;
  isLoading: boolean;
  errors?: APIError[] | null;
  formRef?: React.MutableRefObject<FormikProps<UserFormData> | null>;
}

const USER_ROLES: UserRole[] = [UserRole.EXPERT, UserRole.USER_ADMIN];

export function UserForm(props: Props) {
  const { t } = useTranslation();
  const validationSchema = useUserSchema();

  const initialValues: UserFormData = {
    firstName: props.data.firstName,
    lastName: props.data.lastName,
    email: props.data.email,
    role: props.data.role,
    enabled: props.data.enabled,
  };

  return render();

  function render() {
    return (
      <Formik
        innerRef={props.formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values: UserFormData) => {
          return props.onSubmit(values);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="firstName"
                  label={t("label.firstName")}
                  type="string"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="lastName"
                  label={t("label.lastName")}
                  type="string"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="email"
                  label={t("label.email")}
                  type="email"
                  variant="outlined"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="role"
                  label={t("label.role")}
                  type="text"
                  select={true}
                  variant="outlined"
                  fullWidth
                >
                  <MenuItem value={""}>
                    <em>{t("label.userRole_placeholder")}</em>
                  </MenuItem>
                  {USER_ROLES.map((userRole) => (
                    <MenuItem key={userRole} value={userRole}>
                      {t(`label.userRoles.${userRole}`)}
                    </MenuItem>
                  ))}
                </Field>
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={CheckboxWithLabel}
                  name="enabled"
                  type="checkbox"
                  variant="outlined"
                  Label={{ label: t("label.enabled") }}
                />
              </Grid>
              {props.data.id && (
                <Grid item xs={12}>
                  {t("label.loginLinkLastSent", {
                    date: props.data.dateLastLoginLinkSent,
                  })}
                </Grid>
              )}

              {props.errors && (
                <Grid item xs={12}>
                  <ServerErrors data={props.errors} />
                </Grid>
              )}

              <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                {props.data.id && (
                  <Button variant="text" color="primary" onClick={props.onLoginLinkRequest}>
                    {t("label.sendLoginLink")}
                  </Button>
                )}
                <Button variant="text" onClick={props.onClose}>
                  {t("label.cancel")}
                </Button>
                <ButtonLoading
                  isLoading={isSubmitting || props.isLoading}
                  type="submit"
                  color="primary"
                  variant="contained"
                >
                  {t("label.save")}
                </ButtonLoading>
              </ButtonGrid>
            </Grid>
          </Form>
        )}
      </Formik>
    );
  }
}
